import React, { useMemo } from "react";
import { connect } from "react-redux";

import { ValuePropsBarBlock } from "../../../common/ValuePropsBarBlock";
import { IProduct } from "../../../models/catalogue.interfaces";
import { IProductID } from "../../../models/nominals";
import { IValuePropBarBlock } from "../../../models/value-props.interfaces";
import { IRootState as IReduxState } from "../../reducers.interfaces";
import { rootProductSelector } from "../selectors";

interface IOwnProps {
    blockProductID: IProductID;
    content: string;
}

interface IReduxProps {
    selectedRootProduct: IProduct | null;
}

type IProps = IOwnProps & IReduxProps;

const extractValuePropsData = (content: string): IValuePropBarBlock | null => {
    // Create a temporary DOM element to parse the HTML content
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;

    // Find the element with data-include-react="true"
    const reactIncludeElem = tempDiv.querySelector(
        '[data-include-react="true"]',
    );
    if (!reactIncludeElem) return null;

    // Get the data-props attribute using DOM API
    const dataPropsAttr = reactIncludeElem.getAttribute("data-props");
    if (!dataPropsAttr) return null;

    try {
        // Parse the JSON data - DOM APIs handle HTML entity decoding automatically
        const dataProps = JSON.parse(dataPropsAttr);
        return JSON.parse(dataProps.cms);
    } catch (err) {
        console.warn("Failed to parse value props from content", err);
        return null;
    }
};

const PDPSelectedRootProductConditionalBlockComponent = (
    props: IProps,
): React.ReactNode => {
    // If the currently selected value of the configured attribute doesn't match the desired value, don't render anything
    const isSelected = props.blockProductID === props.selectedRootProduct?.id;
    // Build IDs for relationship to tab controls. These IDs must match the IDs used by PDPChangeRootProductBlock.
    const tabControlID = `change-root-product-block-${props.blockProductID}`;
    const tabPanelID = `root-product-conditional-block-${props.blockProductID}`;

    // Extract and parse the Value Props data from the innerHTML content string.
    // This avoids relying on static dangerouslySetInnerHTML rendering for the value props,
    // allowing React to build and manage the ValuePropsBarBlock component properly.
    // This change fixes issues with event listeners not working due to server-side pre-assembly of HTML.
    const valuePropsData = useMemo(
        () => extractValuePropsData(props.content),
        [props.content],
    );

    return (
        <div
            id={tabPanelID}
            role="tabpanel"
            aria-labelledby={tabControlID}
            aria-hidden={!isSelected}
            style={{ display: isSelected ? "block" : "none" }}
            {...(!valuePropsData && {
                dangerouslySetInnerHTML: { __html: props.content },
            })}
        >
            {valuePropsData && (
                <ValuePropsBarBlock valuePropsData={valuePropsData} />
            )}
        </div>
    );
};

const mapStateToProps = (state: IReduxState, ownProps: IOwnProps): IProps => {
    return {
        selectedRootProduct: rootProductSelector(state.configurator),
        ...ownProps,
    };
};

export const PDPSelectedRootProductConditionalBlock = connect(mapStateToProps)(
    PDPSelectedRootProductConditionalBlockComponent,
);
